'use strict';

// A signed-out visitor who opens a deep link (every notification email links
// one, e.g. #/game-day-practices?focus=<id>&action=keep) sees the login page.
// The router remembers the link here, and the login paths (password and OAuth)
// send the user back to it instead of their home page. sessionStorage, not
// localStorage: it survives the OAuth round trip in the same tab and dies with
// the tab, so a stale link never hijacks a later sign-in.
//
// No DOM imports: this module is unit-tested under node.

const KEY = 'fw_return_route';

// Routes that are part of signing in (or need no account) are never a place to
// come back to; returning there would loop or replay a one-time token.
const AUTH_ROUTES = ['/login', '/register', '/forgot-password', '/reset-password',
  '/confirm-email', '/verify', '/verify-email', '/auth', '/trainer', '/plan'];

// isReturnableRoute reports whether a hash route (with or without its leading
// "#") is a relative in-app route worth returning to after sign-in.
export function isReturnableRoute(hash) {
  if (typeof hash !== 'string') return false;
  const route = hash.replace(/^#/, '');
  if (!route.startsWith('/') || route.startsWith('//')) return false;
  const path = route.split(/[?#]/)[0];
  return !AUTH_ROUTES.some(r => path === r || path.startsWith(r + '/'));
}

function defaultStorage() { return globalThis.sessionStorage; }

// rememberReturnRoute stores the route when it is returnable. Storage can be
// missing or throw (private mode, blocked site data); then nothing is kept.
export function rememberReturnRoute(hash, storage) {
  if (!isReturnableRoute(hash)) return;
  try {
    (storage || defaultStorage()).setItem(KEY, hash.replace(/^#/, ''));
  } catch { /* no storage: the user lands on their home page instead */ }
}

// takeReturnRoute reads the stored route once and removes it. Returns null
// when there is none (or it is not returnable).
export function takeReturnRoute(storage) {
  try {
    const s = storage || defaultStorage();
    const route = s.getItem(KEY);
    s.removeItem(KEY);
    return isReturnableRoute(route) ? route : null;
  } catch {
    return null;
  }
}
